/**
 * Evidence-based option libraries for the Physical Examination dropdowns.
 * Iter 71.
 *
 * Each section provides an array of { value, label, ref, refUrl } objects.
 *   - value: canonical text saved on the record
 *   - label: shown in the picker (often same as value)
 *   - ref:   short evidence reference (e.g. "Hegedus 2012")
 *   - refUrl optional clickable link (PubMed / Cochrane / textbook chapter)
 *
 * The lists are intentionally curated (not exhaustive) — the SmartSelect
 * always includes an "Other (specify)" option for clinicians to write in.
 */

// ─────────── Special Tests grouped by region ───────────
export const SPECIAL_TESTS = {
  shoulder: [
    { value: 'Hawkins-Kennedy', label: 'Hawkins-Kennedy', ref: 'Hegedus 2012' },
    { value: 'Neer impingement', label: "Neer's impingement", ref: 'Hegedus 2012' },
    { value: 'Empty Can (Jobe)', label: 'Empty Can (Jobe)', ref: 'Hanchard 2013' },
    { value: 'Full Can', label: 'Full Can', ref: 'Hanchard 2013' },
    { value: 'External rotation lag sign', label: 'External rotation lag sign', ref: 'Hertel 1996' },
    { value: 'Lift-off (Gerber)', label: 'Lift-off (Gerber)', ref: 'Gerber 1996' },
    { value: "Speed's test", label: "Speed's test", ref: 'Calis 2000' },
    { value: "Yergason's test", label: "Yergason's test", ref: 'Calis 2000' },
    { value: 'Drop-arm test', label: 'Drop-arm test', ref: 'Murrell 2001' },
    { value: 'O\'Brien (Active Compression)', label: "O'Brien (Active Compression)", ref: "O'Brien 1998" },
    { value: 'Apprehension test', label: 'Apprehension test', ref: 'Lo 2004' },
    { value: 'Sulcus sign', label: 'Sulcus sign', ref: 'Tzannes 2002' },
    { value: 'Cross-body adduction', label: 'Cross-body adduction', ref: 'Walton 2004' },
  ],
  elbow_wrist_hand: [
    { value: "Cozen's test",   label: "Cozen's test (lateral epicondylitis)", ref: 'Saroja 2014' },
    { value: 'Mill\'s test',   label: "Mill's test", ref: 'Saroja 2014' },
    { value: 'Polk\'s test',   label: "Polk's test (medial epicondylitis)", ref: 'Polk 2002' },
    { value: 'Tinel sign elbow', label: 'Tinel sign at elbow (ulnar)', ref: 'Buehler 1988' },
    { value: 'Elbow flexion test', label: 'Elbow flexion test', ref: 'Rayan 1992' },
    { value: 'Phalen\'s sign', label: "Phalen's sign", ref: 'Wainner 2005' },
    { value: 'Reverse Phalen', label: 'Reverse Phalen', ref: 'Wainner 2005' },
    { value: 'Tinel sign wrist', label: 'Tinel sign at wrist (median)', ref: 'Wainner 2005' },
    { value: 'Finkelstein test', label: 'Finkelstein test', ref: 'Elliott 1992' },
    { value: 'TFCC compression test', label: 'TFCC compression test', ref: 'Cooney 1993' },
  ],
  hip: [
    { value: 'FABER (Patrick)', label: 'FABER (Patrick)', ref: 'Magee 2014' },
    { value: 'FADIR',           label: 'FADIR', ref: 'Reiman 2013' },
    { value: 'Thomas test',     label: 'Thomas test', ref: 'Harvey 1998' },
    { value: 'Ober test',       label: 'Ober test', ref: 'Reese 2003' },
    { value: 'Trendelenburg',   label: 'Trendelenburg sign', ref: 'Vasudevan 1989' },
    { value: 'Stork stance',    label: 'Stork (single-leg) stance', ref: 'Hegedus 2014' },
    { value: 'Log-roll test',   label: 'Log-roll test (hip)', ref: 'Reiman 2013' },
    { value: 'Hip scour',       label: 'Hip scour (quadrant)', ref: 'Maslowski 2010' },
  ],
  knee: [
    { value: 'Lachman test',          label: 'Lachman test (ACL)', ref: 'Benjaminse 2006' },
    { value: 'Anterior drawer knee',  label: 'Anterior drawer (knee)', ref: 'Benjaminse 2006' },
    { value: 'Pivot shift',           label: 'Pivot shift (ACL)', ref: 'Benjaminse 2006' },
    { value: 'Posterior drawer',      label: 'Posterior drawer (PCL)', ref: 'Rubinstein 1994' },
    { value: "McMurray's test",       label: "McMurray's test (meniscus)", ref: 'Hegedus 2007' },
    { value: 'Thessaly test (20°)',   label: 'Thessaly test (20°)', ref: 'Karachalios 2005' },
    { value: 'Apley compression',     label: 'Apley compression / distraction', ref: 'Hegedus 2007' },
    { value: 'Valgus stress test',    label: 'Valgus stress (MCL)', ref: 'Magee 2014' },
    { value: 'Varus stress test',     label: 'Varus stress (LCL)', ref: 'Magee 2014' },
    { value: 'Clarke sign',           label: 'Clarke sign (patellofemoral)', ref: 'Doberstein 2008' },
    { value: 'Patellar tilt',         label: 'Patellar tilt', ref: 'Watson 2001' },
  ],
  ankle_foot: [
    { value: 'Anterior drawer ankle', label: 'Anterior drawer (ankle ATFL)', ref: 'Vaseenon 2012' },
    { value: 'Talar tilt',            label: 'Talar tilt', ref: 'Vaseenon 2012' },
    { value: 'Squeeze test',          label: 'Squeeze test (high ankle)', ref: 'Sman 2013' },
    { value: 'External rotation stress', label: 'External rotation stress (syndesmosis)', ref: 'Sman 2013' },
    { value: 'Thompson test',         label: "Thompson test (Achilles rupture)", ref: 'Maffulli 1998' },
    { value: 'Windlass test',         label: 'Windlass test (plantar fascia)', ref: 'De Garceau 2003' },
    { value: 'Mulder click',          label: 'Mulder click (Morton neuroma)', ref: 'Owens 2011' },
  ],
  cervical: [
    { value: "Spurling's test",       label: "Spurling's test (radiculopathy)", ref: 'Wainner 2003' },
    { value: 'Cervical distraction',  label: 'Cervical distraction', ref: 'Wainner 2003' },
    { value: 'ULTT 1 (median)',       label: 'ULTT 1 (median nerve)', ref: 'Wainner 2003' },
    { value: 'ULTT 2 (radial)',       label: 'ULTT 2 (radial nerve)', ref: 'Wainner 2003' },
    { value: 'ULTT 3 (ulnar)',        label: 'ULTT 3 (ulnar nerve)', ref: 'Wainner 2003' },
    { value: "Sharp-Purser test",     label: 'Sharp-Purser test (atlanto-axial)', ref: 'Uitvlugt 1988' },
    { value: 'Alar ligament test',    label: 'Alar ligament stress test', ref: 'Kaale 2008' },
    { value: 'Vertebral artery test', label: 'Vertebral artery test', ref: 'Magarey 2004' },
  ],
  thoracic_lumbar_si: [
    { value: 'SLR (Straight-Leg Raise)', label: 'SLR (Straight-Leg Raise)', ref: 'Devillé 2000' },
    { value: 'Slump test',               label: 'Slump test', ref: 'Stankovic 1999' },
    { value: 'Crossed SLR',              label: 'Crossed SLR', ref: 'Devillé 2000' },
    { value: 'Femoral nerve stretch',    label: 'Femoral nerve stretch', ref: 'Magee 2014' },
    { value: 'Gaenslen',                 label: "Gaenslen's test (SIJ)", ref: 'Laslett 2005' },
    { value: 'Distraction test SI',      label: 'Distraction test (SI)', ref: 'Laslett 2005' },
    { value: 'Thigh thrust',             label: 'Thigh thrust (SI)', ref: 'Laslett 2005' },
    { value: 'Compression test SI',      label: 'Compression test (SI)', ref: 'Laslett 2005' },
    { value: 'Sacral thrust',            label: 'Sacral thrust', ref: 'Laslett 2005' },
    { value: 'Stork standing test',      label: 'Stork standing test (SI)', ref: 'Magee 2014' },
    { value: 'Prone instability test',   label: 'Prone instability test', ref: 'Hicks 2003' },
  ],
};

// Flatten for an "All" view if needed
export const ALL_SPECIAL_TESTS = Object.entries(SPECIAL_TESTS).flatMap(([region, list]) =>
  list.map(t => ({ ...t, region }))
);

// ─────────── ROM joints + planes ───────────
export const ROM_JOINTS = [
  { value: 'Cervical flexion',     label: 'Cervical flexion (norm 45-50°)' },
  { value: 'Cervical extension',   label: 'Cervical extension (norm 45-75°)' },
  { value: 'Cervical rotation',    label: 'Cervical rotation (norm 80°)' },
  { value: 'Cervical side-bending',label: 'Cervical side-bending (norm 45°)' },
  { value: 'Shoulder flexion',     label: 'Shoulder flexion (norm 180°)' },
  { value: 'Shoulder abduction',   label: 'Shoulder abduction (norm 180°)' },
  { value: 'Shoulder ER',          label: 'Shoulder external rotation (norm 90°)' },
  { value: 'Shoulder IR',          label: 'Shoulder internal rotation (norm 70-90°)' },
  { value: 'Elbow flexion',        label: 'Elbow flexion (norm 145°)' },
  { value: 'Elbow extension',      label: 'Elbow extension (norm 0°)' },
  { value: 'Forearm supination',   label: 'Forearm supination (norm 85°)' },
  { value: 'Forearm pronation',    label: 'Forearm pronation (norm 80°)' },
  { value: 'Wrist flexion',        label: 'Wrist flexion (norm 80°)' },
  { value: 'Wrist extension',      label: 'Wrist extension (norm 70°)' },
  { value: 'Thoracolumbar flexion',label: 'Thoracolumbar flexion (norm 80°)' },
  { value: 'Lumbar flexion (Schober)', label: 'Lumbar flexion (Modified Schober)' },
  { value: 'Lumbar extension',     label: 'Lumbar extension (norm 25°)' },
  { value: 'Hip flexion',          label: 'Hip flexion (norm 120°)' },
  { value: 'Hip extension',        label: 'Hip extension (norm 30°)' },
  { value: 'Hip abduction',        label: 'Hip abduction (norm 45°)' },
  { value: 'Hip adduction',        label: 'Hip adduction (norm 30°)' },
  { value: 'Hip ER',               label: 'Hip external rotation (norm 45°)' },
  { value: 'Hip IR',               label: 'Hip internal rotation (norm 40°)' },
  { value: 'Knee flexion',         label: 'Knee flexion (norm 135°)' },
  { value: 'Knee extension',       label: 'Knee extension (norm 0°)' },
  { value: 'Ankle dorsiflexion',   label: 'Ankle dorsiflexion (norm 20°)' },
  { value: 'Ankle plantarflexion', label: 'Ankle plantarflexion (norm 50°)' },
  { value: 'Subtalar inversion',   label: 'Subtalar inversion (norm 35°)' },
  { value: 'Subtalar eversion',    label: 'Subtalar eversion (norm 15°)' },
];

// ─────────── MMT muscles (Kendall / Daniels standards) ───────────
export const MMT_MUSCLES = [
  { value: 'Deltoid (anterior)',  label: 'Deltoid (anterior) — shoulder flexion', ref: 'Kendall' },
  { value: 'Deltoid (middle)',    label: 'Deltoid (middle) — shoulder abduction', ref: 'Kendall' },
  { value: 'Deltoid (posterior)', label: 'Deltoid (posterior) — shoulder extension', ref: 'Kendall' },
  { value: 'Supraspinatus',       label: 'Supraspinatus — empty-can', ref: 'Kendall' },
  { value: 'Infraspinatus',       label: 'Infraspinatus — external rotation', ref: 'Kendall' },
  { value: 'Subscapularis',       label: 'Subscapularis — internal rotation', ref: 'Kendall' },
  { value: 'Biceps brachii',      label: 'Biceps brachii — elbow flexion', ref: 'Kendall' },
  { value: 'Triceps brachii',     label: 'Triceps brachii — elbow extension', ref: 'Kendall' },
  { value: 'Wrist flexors',       label: 'Wrist flexors (FCR/FCU)', ref: 'Kendall' },
  { value: 'Wrist extensors',     label: 'Wrist extensors (ECRL/B/ECU)', ref: 'Kendall' },
  { value: 'Iliopsoas',           label: 'Iliopsoas — hip flexion', ref: 'Kendall' },
  { value: 'Gluteus maximus',     label: 'Gluteus maximus — hip extension', ref: 'Kendall' },
  { value: 'Gluteus medius',      label: 'Gluteus medius — hip abduction', ref: 'Kendall' },
  { value: 'Adductors',           label: 'Hip adductors', ref: 'Kendall' },
  { value: 'Quadriceps',          label: 'Quadriceps — knee extension', ref: 'Kendall' },
  { value: 'Hamstrings',          label: 'Hamstrings — knee flexion', ref: 'Kendall' },
  { value: 'Gastrocnemius',       label: 'Gastrocnemius — plantarflexion (standing)', ref: 'Kendall' },
  { value: 'Soleus',              label: 'Soleus — plantarflexion (bent knee)', ref: 'Kendall' },
  { value: 'Tibialis anterior',   label: 'Tibialis anterior — dorsiflexion', ref: 'Kendall' },
  { value: 'Peroneal group',      label: 'Peroneal group — eversion', ref: 'Kendall' },
  { value: 'Tibialis posterior',  label: 'Tibialis posterior — inversion', ref: 'Kendall' },
];

export const MMT_GRADES = [
  { value: '5/5',       label: '5/5 — Full ROM, max resistance' },
  { value: '4+/5',      label: '4+/5 — Full ROM, moderate-strong resistance' },
  { value: '4/5',       label: '4/5 — Full ROM, moderate resistance' },
  { value: '4-/5',      label: '4-/5 — Full ROM, mild resistance' },
  { value: '3+/5',      label: '3+/5 — Full ROM against gravity + minimal resistance' },
  { value: '3/5',       label: '3/5 — Full ROM against gravity, no resistance' },
  { value: '3-/5',      label: '3-/5 — < Full ROM against gravity' },
  { value: '2/5',       label: '2/5 — Full ROM gravity-eliminated' },
  { value: '1/5',       label: '1/5 — Trace contraction only' },
  { value: '0/5',       label: '0/5 — No contraction' },
];

// ─────────── Auscultation findings ───────────
export const AUSC_FINDINGS = [
  { value: 'Vesicular (normal)',  label: 'Vesicular (normal)' },
  { value: 'Bronchial',           label: 'Bronchial' },
  { value: 'Bronchovesicular',    label: 'Bronchovesicular' },
  { value: 'Reduced air entry',   label: 'Reduced air entry' },
  { value: 'Crackles (fine)',     label: 'Crackles — fine (Velcro)' },
  { value: 'Crackles (coarse)',   label: 'Crackles — coarse' },
  { value: 'Wheezes (expiratory)',label: 'Wheezes — expiratory' },
  { value: 'Wheezes (inspiratory)',label:'Wheezes — inspiratory' },
  { value: 'Rhonchi',             label: 'Rhonchi (low-pitched, secretion)' },
  { value: 'Stridor',             label: 'Stridor (upper airway)' },
  { value: 'Pleural friction rub',label: 'Pleural friction rub' },
  { value: 'S1 normal',           label: 'S1 normal' },
  { value: 'S2 normal',           label: 'S2 normal' },
  { value: 'S3 gallop',           label: 'S3 gallop (volume overload)' },
  { value: 'S4 gallop',           label: 'S4 gallop (stiff ventricle)' },
  { value: 'Systolic murmur',     label: 'Systolic murmur' },
  { value: 'Diastolic murmur',    label: 'Diastolic murmur' },
  { value: 'Pericardial rub',     label: 'Pericardial rub' },
  { value: 'Carotid bruit',       label: 'Carotid bruit' },
];

// ─────────── Postural findings ───────────
export const POSTURAL = [
  { value: 'Forward head posture',       label: 'Forward head posture' },
  { value: 'Rounded shoulders',          label: 'Rounded shoulders / protracted scapulae' },
  { value: 'Increased thoracic kyphosis',label: 'Increased thoracic kyphosis' },
  { value: 'Decreased lumbar lordosis',  label: 'Decreased lumbar lordosis (flat back)' },
  { value: 'Increased lumbar lordosis',  label: 'Increased lumbar lordosis (sway-back)' },
  { value: 'Anterior pelvic tilt',       label: 'Anterior pelvic tilt' },
  { value: 'Posterior pelvic tilt',      label: 'Posterior pelvic tilt' },
  { value: 'Lateral pelvic tilt (R higher)', label: 'Lateral pelvic tilt — R iliac crest higher' },
  { value: 'Lateral pelvic tilt (L higher)', label: 'Lateral pelvic tilt — L iliac crest higher' },
  { value: 'Functional scoliosis',       label: 'Functional scoliosis (postural)' },
  { value: 'Structural scoliosis',       label: 'Structural scoliosis (Adams forward bend +)' },
  { value: 'Genu valgum',                label: 'Genu valgum (knock-knee)' },
  { value: 'Genu varum',                 label: 'Genu varum (bow-leg)' },
  { value: 'Genu recurvatum',            label: 'Genu recurvatum (knee hyperextension)' },
  { value: 'Pes planus',                 label: 'Pes planus (flat foot)' },
  { value: 'Pes cavus',                  label: 'Pes cavus (high arch)' },
  { value: 'Foot pronation',             label: 'Foot pronation' },
  { value: 'Foot supination',            label: 'Foot supination' },
  { value: 'Winging scapula',            label: 'Winging scapula' },
];

// ─────────── Balance tests ───────────
export const BALANCE_TESTS = [
  { value: 'Romberg test',           label: 'Romberg test', ref: 'Khasnis 2003' },
  { value: 'Sharpened Romberg',      label: 'Sharpened (tandem) Romberg', ref: 'Black 1982' },
  { value: 'Single-leg stance',      label: 'Single-leg stance (eyes open + closed)', ref: 'Bohannon 1984' },
  { value: 'Tandem walk',            label: 'Tandem walk (10 steps)', ref: 'Cohen 1996' },
  { value: 'Berg Balance Scale',     label: 'Berg Balance Scale (14-item)', ref: 'Berg 1989' },
  { value: 'BESTest',                label: 'BESTest / Mini-BESTest', ref: 'Horak 2009' },
  { value: 'Star Excursion Balance', label: 'Star Excursion Balance Test (SEBT)', ref: 'Plisky 2006' },
  { value: 'Y-Balance Test',         label: 'Y-Balance Test (YBT)', ref: 'Plisky 2009' },
  { value: 'Functional Reach',       label: 'Functional Reach Test', ref: 'Duncan 1990' },
  { value: 'Timed Up and Go',        label: 'Timed Up and Go (TUG)', ref: 'Podsiadlo 1991' },
];

// ─────────── Flexibility tests ───────────
export const FLEXIBILITY_TESTS = [
  { value: 'Sit-and-reach',       label: 'Sit-and-reach (hamstrings + low back)', ref: 'Wells 1952' },
  { value: 'Thomas test',         label: 'Thomas test (hip flexor)', ref: 'Harvey 1998' },
  { value: 'Modified Thomas',     label: 'Modified Thomas (3-segment)', ref: 'Reese 2003' },
  { value: 'Ober test',           label: 'Ober test (ITB)', ref: 'Reese 2003' },
  { value: 'Popliteal angle',     label: 'Popliteal angle (hamstring)', ref: 'Magee 2014' },
  { value: 'AKE (Active Knee Extension)', label: 'Active Knee Extension test', ref: 'Magee 2014' },
  { value: 'Finger-floor distance', label: 'Finger-floor distance', ref: 'Magee 2014' },
  { value: 'Tripod sign',         label: 'Tripod sign (hamstring)', ref: 'Magee 2014' },
  { value: 'Ely test',            label: 'Ely test (rectus femoris)', ref: 'Magee 2014' },
];

// ─────────── End-feel options ───────────
export const ENDFEEL = [
  { value: 'Hard (bony)',       label: 'Hard (bony) — normal at extension lock' },
  { value: 'Firm (capsular)',   label: 'Firm (capsular) — normal at end-range capsule stretch' },
  { value: 'Firm (muscular)',   label: 'Firm (muscular) — normal at muscle stretch' },
  { value: 'Soft (tissue)',     label: 'Soft tissue approximation — normal at flexion limit' },
  { value: 'Empty',             label: 'Empty — pain prevents end-range (pathological)' },
  { value: 'Springy block',     label: 'Springy block — meniscus, loose body (pathological)' },
  { value: 'Spasm',             label: 'Muscle spasm (pathological)' },
  { value: 'Boggy',             label: 'Boggy — effusion (pathological)' },
];

// ─────────── Sides ───────────
export const SIDES = [
  { value: 'R',  label: 'Right' },
  { value: 'L',  label: 'Left'  },
  { value: 'B',  label: 'Bilateral' },
];

export const POS_NEG = [
  { value: 'positive',     label: 'Positive (+)' },
  { value: 'negative',     label: 'Negative (−)' },
  { value: 'equivocal',    label: 'Equivocal' },
  { value: 'not_tested',   label: 'Not tested' },
];
